/**
 * Onboarding Data Page
 * Admin portal page to view customer and vendor onboarding submissions
 */

import { useState, useMemo, useRef, useEffect, useLayoutEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { ExportDataModal } from '../../components/shared/ExportDataModal';
import { useToast } from '../../components/shared/Toast';
import { MdFilterList, MdSearch, MdRefresh, MdDownload, MdCheckCircle, MdCancel, MdVisibility, MdDelete, MdPayments, MdMoreVert } from 'react-icons/md';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';
import { cn } from '../../lib/utils';

// Use relative URL in development (with Vite proxy) or env var, otherwise default to localhost:3000
const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

function filenameFromDisposition(header: string | null, fallback: string): string {
  if (!header) return fallback;
  const utfMatch = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utfMatch?.[1]) return decodeURIComponent(utfMatch[1]);
  const match = header.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function OnboardingRowActions({
  disabled,
  canApprove,
  canReject,
  needsPaymentApproval,
  onView,
  onDelete,
  onApprove,
  onReject,
  onAcceptPayment,
}: {
  disabled?: boolean;
  canApprove: boolean;
  canReject: boolean;
  needsPaymentApproval: boolean;
  onView: () => void;
  onDelete: () => void;
  onApprove: () => void;
  onReject: () => void;
  onAcceptPayment: () => void;
}) {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 180;
    let left = rect.right - width;
    if (left < 8) left = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < 180;
    setMenuStyle({
      position: 'fixed',
      zIndex: 2300,
      top: openUp ? undefined : rect.bottom + 4,
      bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
      left,
      width,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onMove = () => updateMenuPosition();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    return () => {
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (buttonRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const menuItemClass =
    'flex w-full items-center gap-2 px-3 py-2 text-sm text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] disabled:opacity-50 disabled:cursor-not-allowed';

  return (
    <div className="flex justify-center" onClick={(e) => e.stopPropagation()}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="Onboarding actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="p-2 rounded-lg text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))] transition-colors disabled:opacity-50"
      >
        <MdMoreVert className="w-5 h-5" />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle}
              className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] py-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                className={menuItemClass}
                onClick={() => {
                  setOpen(false);
                  onView();
                }}
              >
                <MdVisibility className="w-4 h-4 text-[#0088cc]" />
                View
              </button>
              {needsPaymentApproval && (
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  disabled={disabled}
                  onClick={() => {
                    setOpen(false);
                    onAcceptPayment();
                  }}
                >
                  <MdPayments className="w-4 h-4 text-amber-600" />
                  Accept Payment
                </button>
              )}
              {canApprove && (
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  disabled={disabled}
                  onClick={() => {
                    setOpen(false);
                    onApprove();
                  }}
                >
                  <MdCheckCircle className="w-4 h-4 text-emerald-600" />
                  Approve
                </button>
              )}
              {canReject && (
                <button
                  type="button"
                  role="menuitem"
                  className={menuItemClass}
                  disabled={disabled}
                  onClick={() => {
                    setOpen(false);
                    onReject();
                  }}
                >
                  <MdCancel className="w-4 h-4 text-[hsl(var(--destructive))]" />
                  Reject
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className={cn(menuItemClass, 'text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30')}
                disabled={disabled}
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
              >
                <MdDelete className="w-4 h-4" />
                Delete
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

interface CustomerOnboarding {
  _id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  status: 'pending' | 'pending_payment' | 'completed' | 'approved' | 'rejected';
  paymentStatus?: string;
  paymentMethod?: string;
  submittedAt?: string;
  organizationId?: string;
}

interface VendorOnboarding {
  _id: string;
  companyName: string;
  contactPerson: string;
  email: string;
  status: 'pending' | 'completed' | 'approved' | 'rejected';
  submittedAt?: string;
  organizationId?: string;
  vendorCategory?: 'global' | 'contracted';
  vendorCategoryLabel?: string;
  invitedByCustomerName?: string | null;
  requiresAdminApproval?: boolean;
}

export function OnboardingDataPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [filterType, setFilterType] = useState<'all' | 'customer' | 'vendor'>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [vendorTypeFilter, setVendorTypeFilter] = useState<'all' | 'global' | 'contracted'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
    queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
    setTimeout(() => {
      setIsRefreshing(false);
      showToast('Onboarding data refreshed', 'success');
    }, 1000);
  };

  const handleExport = async (format: 'excel' | 'pdf') => {
    const params = new URLSearchParams();
    params.set('format', format);
    if (filterType !== 'all') params.set('type', filterType);
    if (filterStatus !== 'all') params.set('status', filterStatus);
    if (vendorTypeFilter !== 'all') params.set('vendorScope', vendorTypeFilter);
    if (searchQuery.trim()) params.set('search', searchQuery.trim());

    const url = API_URL
      ? `${API_URL}/api/v1/admin/onboardings/export?${params}`
      : `/api/v1/admin/onboardings/export?${params}`;
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to export onboarding data');
    }

    const blob = await response.blob();
    const fallback = `onboarding-submissions.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
    const filename = filenameFromDisposition(response.headers.get('Content-Disposition'), fallback);
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);
    showToast(`Onboarding data exported as ${format === 'pdf' ? 'PDF' : 'Excel'}`, 'success');
  };

  // Approve customer onboarding mutation
  const approveCustomerMutation = useMutation({
    mutationFn: async (id: string) => {
      const url = API_URL ? `${API_URL}/api/v1/admin/customer-onboardings/${id}/approve` : `/api/v1/admin/customer-onboardings/${id}/approve`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Failed to approve onboarding';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (e) {
          // If parsing fails, use default error message
        }
        throw new Error(errorMessage);
      }

      try {
        if (text && isJson) {
          return JSON.parse(text);
        }
        return {};
      } catch (e) {
        return {};
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['organizations-with-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      showToast('Onboarding approved successfully. License has been created automatically.', 'success');
    },
    onError: (error: Error) => {
      console.error('Approve customer onboarding error:', error);
      const errorMessage = error.message || 'Failed to approve onboarding';
      showToast(errorMessage, 'error');
    },
  });

  // Reject customer onboarding mutation
  const rejectCustomerMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const url = API_URL ? `${API_URL}/api/v1/admin/customer-onboardings/${id}/reject` : `/api/v1/admin/customer-onboardings/${id}/reject`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ rejectionReason: reason }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Failed to reject onboarding';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (e) {
          // If parsing fails, use default error message
        }
        throw new Error(errorMessage);
      }

      try {
        if (text && isJson) {
          return JSON.parse(text);
        }
        return {};
      } catch (e) {
        return {};
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      showToast('Onboarding rejected successfully', 'success');
    },
    onError: (error: Error) => {
      console.error('Reject onboarding error:', error);
      const errorMessage = error.message || 'Failed to reject onboarding';
      showToast(errorMessage, 'error');
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const url = API_URL ? `${API_URL}/api/v1/admin/customer-onboardings/${id}` : `/api/v1/admin/customer-onboardings/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ reason }),
      });
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      if (!response.ok) {
        let errorMessage = 'Failed to delete onboarding';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (_e) {}
        throw new Error(errorMessage);
      }
      return text && isJson ? JSON.parse(text) : {};
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      showToast('Customer onboarding deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete onboarding', 'error');
    },
  });

  const deleteVendorMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) => {
      const url = API_URL ? `${API_URL}/api/v1/admin/vendor-onboardings/${id}` : `/api/v1/admin/vendor-onboardings/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ reason }),
      });
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      if (!response.ok) {
        let errorMessage = 'Failed to delete vendor onboarding';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (_e) {}
        throw new Error(errorMessage);
      }
      return text && isJson ? JSON.parse(text) : {};
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
      showToast('Vendor onboarding deleted successfully', 'success');
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to delete vendor onboarding', 'error');
    },
  });

  const approveCustomerPaymentMutation = useMutation({
    mutationFn: async (id: string) => {
      const url = API_URL
        ? `${API_URL}/api/v1/admin/customer-onboardings/${id}/approve-payment`
        : `/api/v1/admin/customer-onboardings/${id}/approve-payment`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      if (!response.ok) {
        let errorMessage = 'Failed to approve payment';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (_e) {}
        throw new Error(errorMessage);
      }
      return text && isJson ? JSON.parse(text) : {};
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      const emailFailed = data?.data?.emailError && !data?.data?.emailSent;
      showToast(
        data.message || 'Payment approved and account activated',
        emailFailed ? 'error' : 'success'
      );
    },
    onError: (error: Error) => {
      showToast(error.message || 'Failed to approve payment', 'error');
    },
  });

  // Approve vendor onboarding mutation
  const approveVendorMutation = useMutation({
    mutationFn: async (id: string) => {
      const url = API_URL ? `${API_URL}/api/v1/admin/vendor-onboardings/${id}/approve` : `/api/v1/admin/vendor-onboardings/${id}/approve`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Failed to approve onboarding';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (e) {
          // If parsing fails, use default error message
        }
        throw new Error(errorMessage);
      }

      try {
        if (text && isJson) {
          return JSON.parse(text);
        }
        return {};
      } catch (e) {
        return {};
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['customer-onboardings'] });
      queryClient.invalidateQueries({ queryKey: ['organizations-with-licenses'] });
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
      showToast('Onboarding approved successfully. License has been created automatically.', 'success');
    },
    onError: (error: Error) => {
      console.error('Approve customer onboarding error:', error);
      const errorMessage = error.message || 'Failed to approve onboarding';
      showToast(errorMessage, 'error');
    },
  });

  // Reject vendor onboarding mutation
  const rejectVendorMutation = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const url = API_URL ? `${API_URL}/api/v1/admin/vendor-onboardings/${id}/reject` : `/api/v1/admin/vendor-onboardings/${id}/reject`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({ rejectionReason: reason }),
      });

      const contentType = response.headers.get('content-type');
      const isJson = contentType && contentType.includes('application/json');
      const text = await response.text();
      
      if (!response.ok) {
        let errorMessage = 'Failed to reject onboarding';
        try {
          if (text && isJson) {
            const error = JSON.parse(text);
            errorMessage = error.error || errorMessage;
          } else if (text) {
            errorMessage = text;
          }
        } catch (e) {
          // If parsing fails, use default error message
        }
        throw new Error(errorMessage);
      }

      try {
        if (text && isJson) {
          return JSON.parse(text);
        }
        return {};
      } catch (e) {
        return {};
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor-onboardings'] });
      showToast('Onboarding rejected successfully', 'success');
    },
    onError: (error: Error) => {
      console.error('Reject onboarding error:', error);
      const errorMessage = error.message || 'Failed to reject onboarding';
      showToast(errorMessage, 'error');
    },
  });

  const handleApproveCustomer = (id: string) => {
    if (window.confirm('Are you sure you want to approve this onboarding? A license will be created automatically.')) {
      approveCustomerMutation.mutate(id);
    }
  };

  const handleRejectCustomer = (id: string) => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    if (reason !== null) {
      rejectCustomerMutation.mutate({ id, reason: reason || undefined });
    }
  };

  const handleApproveVendor = (id: string) => {
    if (window.confirm('Are you sure you want to approve this onboarding? A license will be created automatically.')) {
      approveVendorMutation.mutate(id);
    }
  };

  const handleRejectVendor = (id: string) => {
    const reason = window.prompt('Please provide a reason for rejection (optional):');
    if (reason !== null) {
      rejectVendorMutation.mutate({ id, reason: reason || undefined });
    }
  };

  const handleDeleteCustomer = (id: string) => {
    const reason = window.prompt('Please provide a reason for deletion (required):');
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      showToast('Deletion reason is required', 'error');
      return;
    }
    if (window.confirm('Are you sure you want to delete this customer onboarding record?')) {
      deleteCustomerMutation.mutate({ id, reason: trimmed });
    }
  };

  const handleDeleteVendor = (id: string) => {
    const reason = window.prompt('Please provide a reason for deletion (required):');
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      showToast('Deletion reason is required', 'error');
      return;
    }
    if (window.confirm('Are you sure you want to delete this vendor onboarding record?')) {
      deleteVendorMutation.mutate({ id, reason: trimmed });
    }
  };

  const handleAcceptCustomerPayment = (id: string) => {
    if (
      window.confirm(
        'Accept this payment and activate the customer account? Login credentials will be emailed to the customer.'
      )
    ) {
      approveCustomerPaymentMutation.mutate(id);
    }
  };

  const customerNeedsPaymentApproval = (item: CustomerOnboarding) =>
    item.status !== 'approved' &&
    item.status !== 'rejected' &&
    (item.status === 'pending_payment' ||
      item.paymentStatus === 'pending' ||
      (Boolean(item.paymentStatus) && item.paymentStatus !== 'paid'));

  // Fetch customer onboardings
  const { data: customerOnboardings, isLoading: customerLoading, error: customerError } = useQuery<CustomerOnboarding[]>({
    queryKey: ['customer-onboardings', filterStatus],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filterStatus !== 'all') {
          params.append('status', filterStatus);
        }

        const url = API_URL ? `${API_URL}/api/v1/admin/customer-onboardings?${params}` : `/api/v1/admin/customer-onboardings?${params}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to fetch customer onboardings' }));
          console.error('Failed to fetch customer onboardings:', error);
          throw new Error(error.error || 'Failed to fetch customer onboardings');
        }
        const data = await response.json();
        console.log('Customer onboardings data:', data);
        return data.data || [];
      } catch (error: any) {
        console.error('Error fetching customer onboardings:', error);
        throw error;
      }
    },
    enabled: filterType === 'all' || filterType === 'customer',
    retry: 1,
  });

  // Fetch vendor onboardings
  const { data: vendorOnboardings, isLoading: vendorLoading, error: vendorError } = useQuery<VendorOnboarding[]>({
    queryKey: ['vendor-onboardings', filterStatus, vendorTypeFilter],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (filterStatus !== 'all') {
          params.append('status', filterStatus);
        }
        if (vendorTypeFilter !== 'all') {
          params.append('vendorScope', vendorTypeFilter);
        }

        const url = API_URL ? `${API_URL}/api/v1/admin/vendor-onboardings?${params}` : `/api/v1/admin/vendor-onboardings?${params}`;
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
          },
        });

        if (!response.ok) {
          const error = await response.json().catch(() => ({ error: 'Failed to fetch vendor onboardings' }));
          console.error('Failed to fetch vendor onboardings:', error);
          throw new Error(error.error || 'Failed to fetch vendor onboardings');
        }
        const data = await response.json();
        console.log('Vendor onboardings data:', data);
        return data.data || [];
      } catch (error: any) {
        console.error('Error fetching vendor onboardings:', error);
        throw error;
      }
    },
    enabled: filterType === 'all' || filterType === 'vendor',
    retry: 1,
  });

  const isLoading = customerLoading || vendorLoading;

  // Combine data based on filter
  const allOnboardings = useMemo(() => {
    const data: Array<(CustomerOnboarding | VendorOnboarding) & { type: 'customer' | 'vendor' }> = [];
    
    if (filterType === 'all' || filterType === 'customer') {
      (customerOnboardings || []).forEach((item) => {
        data.push({ ...item, type: 'customer' });
      });
    }
    
    if (filterType === 'all' || filterType === 'vendor') {
      (vendorOnboardings || []).forEach((item) => {
        data.push({ ...item, type: 'vendor' });
      });
    }

    return data;
  }, [customerOnboardings, vendorOnboardings, filterType]);

  // Filter by search query
  const filteredAllOnboardings = useMemo(() => {
    if (!searchQuery) return allOnboardings;
    const query = searchQuery.toLowerCase();
    return allOnboardings.filter(item =>
      item.companyName.toLowerCase().includes(query) ||
      item.contactPerson.toLowerCase().includes(query) ||
      item.email.toLowerCase().includes(query)
    );
  }, [allOnboardings, searchQuery]);

  const handleOnboardingClick = (id: string, type: 'customer' | 'vendor') => {
    navigate(`/onboarding-data/${id}?type=${type}`);
  };

  const combinedColumns = [
    {
      key: 'type',
      header: 'Type',
      render: (item: any) => (
        <span
          className={cn(
            'px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full',
            item.type === 'customer'
              ? 'bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50 ring-1 ring-blue-200 dark:ring-blue-800'
              : 'bg-purple-100 text-[hsl(var(--foreground))] font-semibold dark:bg-purple-900/50 ring-1 ring-purple-200 dark:ring-purple-800'
          )}
        >
          {item.type === 'customer' ? 'Customer' : 'Vendor'}
        </span>
      ),
    },
    {
      key: 'companyName',
      header: 'Company Name',
      render: (item: any) => (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleOnboardingClick(item._id, item.type);
          }}
          className={`font-semibold text-[hsl(var(--foreground))] dark:text-white transition-colors text-left ${
            item.type === 'customer'
              ? 'hover:text-[#0088cc] dark:hover:text-blue-400'
              : 'hover:text-purple-600 dark:hover:text-purple-400'
          }`}
        >
          {item.companyName}
        </button>
      ),
    },
    {
      key: 'contactPerson',
      header: 'Contact Person',
      render: (item: any) => (
        <span className="text-[hsl(var(--muted-foreground))]">{item.contactPerson}</span>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      render: (item: any) => (
        <span className="text-[hsl(var(--muted-foreground))]">{item.email}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item: any) => {
        const statusColors: Record<string, string> = {
          pending: 'bg-amber-100 text-[hsl(var(--foreground))] font-semibold dark:bg-amber-900/50 ring-amber-200 dark:ring-amber-800',
          pending_payment: 'bg-orange-100 text-orange-800 font-semibold dark:bg-orange-900/50 ring-orange-200 dark:ring-orange-800',
          completed: 'bg-blue-100 text-[hsl(var(--foreground))] font-semibold dark:bg-blue-900/50 ring-blue-200 dark:ring-blue-800',
          approved: 'bg-emerald-100 text-[hsl(var(--foreground))] font-semibold dark:bg-emerald-900/50 ring-emerald-200 dark:ring-emerald-800',
          rejected: 'bg-red-100 text-[hsl(var(--foreground))] font-semibold dark:bg-red-900/50 ring-red-200 dark:ring-red-800',
        };
        const label = item.status === 'pending_payment'
          ? 'Payment Pending'
          : item.status.charAt(0).toUpperCase() + item.status.slice(1);
        return (
          <span className={cn('px-3 py-1 inline-flex text-xs leading-5 font-bold rounded-full ring-1', statusColors[item.status] || statusColors.pending)}>
            {label}
          </span>
        );
      },
    },
    {
      key: 'submittedAt',
      header: 'Submitted At',
      render: (item: any) => (
        <span className="text-[hsl(var(--muted-foreground))]">
          {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      className: 'w-16 text-center',
      render: (item: any) => {
        const needsPaymentApproval =
          item.type === 'customer' && customerNeedsPaymentApproval(item);
        const canApproveReject =
          !needsPaymentApproval &&
          (item.status === 'completed' || item.status === 'pending') &&
          (item.type !== 'vendor' || item.requiresAdminApproval !== false);
        const canReject =
          item.type === 'customer'
            ? item.status === 'completed' ||
              item.status === 'pending' ||
              item.status === 'pending_payment' ||
              needsPaymentApproval
            : canApproveReject;
        const isProcessing =
          approveCustomerMutation.isPending ||
          rejectCustomerMutation.isPending ||
          approveVendorMutation.isPending ||
          rejectVendorMutation.isPending ||
          deleteCustomerMutation.isPending ||
          deleteVendorMutation.isPending ||
          approveCustomerPaymentMutation.isPending;

        return (
          <OnboardingRowActions
            disabled={isProcessing}
            canApprove={canApproveReject}
            canReject={canReject}
            needsPaymentApproval={needsPaymentApproval}
            onView={() => handleOnboardingClick(item._id, item.type)}
            onDelete={() =>
              item.type === 'customer'
                ? handleDeleteCustomer(item._id)
                : handleDeleteVendor(item._id)
            }
            onApprove={() =>
              item.type === 'customer'
                ? handleApproveCustomer(item._id)
                : handleApproveVendor(item._id)
            }
            onReject={() =>
              item.type === 'customer'
                ? handleRejectCustomer(item._id)
                : handleRejectVendor(item._id)
            }
            onAcceptPayment={() => handleAcceptCustomerPayment(item._id)}
          />
        );
      },
    },
  ];

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-[hsl(var(--foreground))] mb-2 bg-[#0088cc] hover:bg-[#0077b3] transition-all bg-clip-text text-transparent">
            Onboarding Data
          </h1>
          <p className="text-lg text-[hsl(var(--muted-foreground))] font-medium">
            View customer and vendor onboarding submissions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors text-sm font-medium text-[hsl(var(--foreground))] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <MdRefresh className={cn('w-4 h-4', isRefreshing && 'animate-spin')} />
            Refresh
          </button>
          <button
            onClick={() => setIsExportOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[hsl(var(--card))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))] transition-colors text-sm font-medium text-[hsl(var(--foreground))]"
          >
            <MdDownload className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="p-6 rounded-2xl bg-[hsl(var(--card))]/80 backdrop-blur-xl border border-[hsl(var(--border))]/50 shadow-lg">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="flex items-center gap-3 px-4 py-2 rounded-xl bg-[hsl(var(--secondary))] border border-[hsl(var(--border))] focus-within:border-[hsl(var(--primary))] focus-within:ring-2 focus-within:ring-[hsl(var(--primary))]/20 transition-all">
            <MdSearch className="w-5 h-5 text-[hsl(var(--muted-foreground))] flex-shrink-0" />
            <input
              type="text"
              placeholder="Search by company name, contact person, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 min-w-0 bg-transparent !border-0 !shadow-none outline-none !ring-0 focus:!border-0 focus:!outline-none focus:!ring-0 focus:!shadow-none text-sm text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))]"
            />
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-center gap-2 text-[hsl(var(--foreground))] font-semibold">
              <MdFilterList className="w-5 h-5" />
              <span>Filters:</span>
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all duration-200 font-medium"
            >
              <option value="all">All Types</option>
              <option value="customer">Customer Only</option>
              <option value="vendor">Vendor Only</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-[hsl(var(--primary))] focus:border-[hsl(var(--primary))] transition-all duration-200 font-medium"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="pending_payment">Payment Pending</option>
              <option value="completed">Completed</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            {(filterType === 'all' || filterType === 'vendor') && (
              <select
                value={vendorTypeFilter}
                onChange={(e) => setVendorTypeFilter(e.target.value as 'all' | 'global' | 'contracted')}
                className="px-4 py-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200 font-medium"
              >
                <option value="all">All Vendor Types</option>
                <option value="global">Global Vendors (Admin approval)</option>
                <option value="contracted">Contracted Vendors (Auto-approved)</option>
              </select>
            )}
            <ClearFiltersButton
              visible={
                !!searchQuery.trim() ||
                filterType !== 'all' ||
                filterStatus !== 'all' ||
                vendorTypeFilter !== 'all'
              }
              onClick={() => {
                setSearchQuery('');
                setFilterType('all');
                setFilterStatus('all');
                setVendorTypeFilter('all');
              }}
              variant="toolbar"
            />
          </div>
        </div>
      </div>

      {/* Tables */}
      {isLoading ? (
        <PageSkeleton variant="table" />
      ) : (
        <div className="p-6 rounded-2xl bg-[hsl(var(--card))]/80 backdrop-blur-xl border border-[hsl(var(--border))]/50 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Showing {filteredAllOnboardings.length} onboarding{filteredAllOnboardings.length !== 1 ? 's' : ''}
            </p>
          </div>
          {(filterType === 'all' || filterType === 'customer') && customerError && (
            <div className="mb-4 p-3 rounded-lg bg-[hsl(var(--destructive))]/10 border border-red-200 dark:border-red-800 text-[hsl(var(--destructive))] text-sm">
              Error loading customer onboardings: {customerError instanceof Error ? customerError.message : 'Unknown error'}
            </div>
          )}
          {(filterType === 'all' || filterType === 'vendor') && vendorError && (
            <div className="mb-4 p-3 rounded-lg bg-[hsl(var(--destructive))]/10 border border-red-200 dark:border-red-800 text-[hsl(var(--destructive))] text-sm">
              Error loading vendor onboardings: {vendorError instanceof Error ? vendorError.message : 'Unknown error'}
            </div>
          )}
          <DataTable
            columns={combinedColumns}
            data={filteredAllOnboardings}
            onRowClick={(item) => handleOnboardingClick(item._id, item.type)}
            emptyMessage="No onboarding data found."
          />
        </div>
      )}

      <ExportDataModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        onExport={async (format) => {
          try {
            await handleExport(format);
          } catch (error: any) {
            showToast(error.message || 'Failed to export onboarding data', 'error');
            throw error;
          }
        }}
        recordCount={filteredAllOnboardings.length}
        title="Export onboarding data"
        entityLabel="submission"
      />
    </div>
  );
}




